import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal, untracked } from '@angular/core';

import { AuthService, UserService } from '@evc/platform';

import { catchError, EMPTY, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonComponent, FrameComponent, Maybe, ToastService, IconComponent } from '@evc/web-components';

import { Pastry, PastryDto } from '@app/models/pastry.model';
import { PastryService } from '@app/services/database/pastry.service';
import { InventoryService } from '@app/services/storage/inventory.service';

@Component({
  selector: 'app-private-page',
  standalone: true,
  imports: [ButtonComponent, FrameComponent, IconComponent],
  schemas: [],
  templateUrl: './private-page.component.html',
  styleUrl: './private-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivatePageComponent {
  readonly #authService = inject(AuthService);
  readonly #inventoryService = inject(InventoryService);
  readonly #pastryService = inject(PastryService);
  readonly #toastService: ToastService = inject(ToastService);
  readonly #userService = inject(UserService);
  readonly #destroyRef = inject(DestroyRef);

  isAuthenticated = this.#authService.isAuthenticated;
  userName = computed<Maybe<string>>(() => this.#userService.profile()?.displayName);

  readonly pastries = signal<Pastry[]>([]);

  chocolateCroissant: PastryDto = {
    name: 'Chocolate Croissant',
    isSweet: true,
    isSavory: false,
    tastynessScore: 10,
    };

  constructor() {
    effect(() => {
      if (!this.isAuthenticated()) return;

      untracked(() => this.getInventory());
    }, { allowSignalWrites: true });
  }

  addChocolateCroissant(): void {
    this.#pastryService.addPastry$(this.chocolateCroissant)
    .pipe(
      switchMap(() => this.#pastryService.getPastries$()),
      tap((inventory) => {
        this.pastries.set(inventory);
        this.#toastService.success('Chocolate croissant baked successfully!');
      }),
      catchError(() => {
        this.#toastService.error('Oops, could not bake a chocolate croissant!');
        return EMPTY;
      }),
      takeUntilDestroyed(this.#destroyRef)
    )
    .subscribe();
  }


  eatPastries(): void {
    if (!this.pastries().length) {
      this.#toastService.warning('No pastries to eat!');
      return;
    }

    this.#pastryService.deletePastries$()
      .pipe(
        switchMap(() => this.#pastryService.getPastries$()),
        tap((inventory) => {
          this.pastries.set(inventory);
          this.#toastService.success('Pastries eaten successfully!');
        }),
        catchError(() => {
          this.#toastService.error('Oops, could not eat pastries!');
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  downloadInventory(): void {
    this.#inventoryService.downloadBlob$('bakery', 'inventory.json')
      .pipe(
        tap((textContent) => {
          const blob = new Blob([textContent], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'inventory.json';
          a.click();
          window.URL.revokeObjectURL(url);
          this.#toastService.success('Inventory downloaded successfully!');
        }),
        catchError(() => {
          this.#toastService.error('Oops, could not download inventory!');
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  getInventory(showSuccessToast: boolean = false): void {
    this.#pastryService.getPastries$()
      .pipe(
        tap((inventory) => {
          this.pastries.set(inventory);
          if (showSuccessToast) {
            this.#toastService.success('Inventory fetched successfully!');
          }
        }),
        catchError(() => {
          this.#toastService.error('Oops, could not get inventory!');
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  uploadInventory(): void {
    const containerName = 'bakery';
    const blobName = 'inventory.json';
    const content = this.pastries();
    const overwrite = true;

    this.#inventoryService.uploadBlob$<Pastry[]>(containerName, blobName, content, overwrite)
      .pipe(
        tap(() => {
          this.#toastService.success('Inventory uploaded successfully!');
        }),
        catchError(() => {
          this.#toastService.error('Oops, could not upload inventory!');
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }
}
